import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, UserPlus, Search, Grid3x3, List, LayoutGrid, Upload, Download, FileSpreadsheet, Eye, X, AlertCircle, AlertTriangle } from 'lucide-react';
import { checkStudentRequiredFields, checkTeacherRequiredFields, getEmptyFieldsTooltip } from '../utils/profileValidator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

// Helper function to extract error message from API response
const getErrorMessage = (error, fallback = 'Operation failed') => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (Array.isArray(detail)) {
      return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
    } else if (typeof detail === 'object') {
      return detail.msg || JSON.stringify(detail);
    }
    return detail;
  }
  return fallback;
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [centers, setCenters] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeRole, setActiveRole] = useState('teacher');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [importErrors, setImportErrors] = useState([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [viewInfoDialog, setViewInfoDialog] = useState(false);
  const [selectedUserInfo, setSelectedUserInfo] = useState(null);
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);

  const isValidDate = (dateValue) => {
    if (!dateValue) return false;
    const date = new Date(dateValue);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'teacher',
    batch_id: '',
    center_id: '',
    is_center_head: false,
    gk_code: '',
    date_of_birth: null,
    date_of_joining: null,
    module: '',
    group: '',
    status: 'active',
    gender: '',
    father_name: '',
    mother_name: '',
    contact_number: '',
    alternate_contact_number: '',
    student_mode: '',
    payment_term: '',
    full_address: '',
    postal_code: '',
    school_name: '',
    grade: '',
    request_kit: false,
    kit_notes: ''
  });

  useEffect(() => {
    loadBatches();
    loadCenters();
    loadUsers(activeRole);
  }, [activeRole]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredUsers(users);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = users.filter(user => {
      const matchName = user.name?.toLowerCase().includes(term);
      const matchEmail = user.email?.toLowerCase().includes(term);
      
      if (user.role === 'student') {
        const matchGKCode = user.gk_code?.toLowerCase().includes(term);
        const matchModule = user.module?.toLowerCase().includes(term);
        const matchStatus = user.status?.toLowerCase().includes(term);
        const matchCenter = centers.find(c => c.id === user.center_id)?.name?.toLowerCase().includes(term);
        return matchName || matchEmail || matchGKCode || matchModule || matchStatus || matchCenter;
      } else if (user.role === 'teacher') {
        const matchCenter = centers.find(c => c.id === user.center_id)?.name?.toLowerCase().includes(term);
        const matchCenterHead = user.is_center_head && 'center head'.includes(term);
        return matchName || matchEmail || matchCenter || matchCenterHead;
      }
      
      return matchName || matchEmail;
    });
    setFilteredUsers(filtered);
  }, [searchTerm, users, centers]);

  const loadBatches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(response.data);
    } catch (error) {
      console.error('Error loading batches:', error);
    }
  };

  const loadCenters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/centers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCenters(response.data);
    } catch (error) {
      console.error('Error loading centers:', error);
    }
  };

  const loadUsers = async (role) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users?role=${role}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔍 Form submitted, starting validation...');
    try {
      const token = localStorage.getItem('token');
      const payload = { ...formData };
      console.log('📦 Payload before processing:', payload);
      
      if (!payload.password) {
        delete payload.password;
      }

      // Convert Date objects to ISO strings with validation
      if (payload.date_of_birth) {
        if (payload.date_of_birth instanceof Date && !isNaN(payload.date_of_birth.getTime())) {
          payload.date_of_birth = payload.date_of_birth.toISOString();
        } else if (typeof payload.date_of_birth === 'string') {
          // Already a string, validate it
          const testDate = new Date(payload.date_of_birth);
          if (isNaN(testDate.getTime())) {
            payload.date_of_birth = null;
          }
        }
      }
      if (payload.date_of_joining) {
        if (payload.date_of_joining instanceof Date && !isNaN(payload.date_of_joining.getTime())) {
          payload.date_of_joining = payload.date_of_joining.toISOString();
        } else if (typeof payload.date_of_joining === 'string') {
          const testDate = new Date(payload.date_of_joining);
          if (isNaN(testDate.getTime())) {
            payload.date_of_joining = null;
          }
        }
      }

      console.log('📦 Payload after date processing:', payload);

      if (editingUser) {
        console.log('🔄 Updating user:', editingUser.id);
        await axios.put(`${API}/users/${editingUser.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ User updated successfully');
        toast.success('User updated successfully');
      } else {
        console.log('➕ Creating new user');
        await axios.post(`${API}/users`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('✅ User created successfully');
        toast.success('User created successfully');
      }
      
      console.log('🔄 Closing dialog and reloading users...');
      setIsDialogOpen(false);
      setEditingUser(null);
      await loadUsers(activeRole);
      resetForm();
      console.log('✅ All done!');
    } catch (error) {
      console.error('❌ Error saving user:', error);
      console.error('❌ Error details:', error.message, error.stack);
      toast.error(getErrorMessage(error, 'Failed to save user'));
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      ...user,
      password: '',
      date_of_birth: user.date_of_birth ? new Date(user.date_of_birth) : null,
      date_of_joining: user.date_of_joining ? new Date(user.date_of_joining) : null,
      request_kit: false,
      kit_notes: ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      loadUsers(activeRole);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: activeRole,
      batch_id: '',
      center_id: '',
      is_center_head: false,
      gk_code: '',
      date_of_birth: null,
      date_of_joining: null,
      module: '',
      group: '',
      status: 'active',
      gender: '',
      father_name: '',
      mother_name: '',
      contact_number: '',
      alternate_contact_number: '',
      student_mode: '',
      payment_term: '',
      full_address: '',
      postal_code: '',
      school_name: '',
      grade: '',
      request_kit: false,
      kit_notes: ''
    });
  };

  const handleFileUpload = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append('file', importFile);
    formDataObj.append('update_existing', updateExisting);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/users/import/excel`,
        formDataObj,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const { successful_imports, failed_imports, errors } = response.data;

      if (failed_imports > 0 && errors) {
        setImportErrors(errors);
        setShowErrorDialog(true);
      }

      if (successful_imports > 0) {
        toast.success(`Successfully imported ${successful_imports} user(s)`);
        loadUsers(activeRole);
      }

      if (failed_imports > 0) {
        toast.warning(`Failed to import ${failed_imports} user(s). Check errors for details.`);
      }

      setImportDialog(false);
      setImportFile(null);
      setUpdateExisting(false);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(getErrorMessage(error, 'Failed to import users'));
    }
  };

  const handleViewInfo = (user) => {
    setSelectedUserInfo(user);
    setViewInfoDialog(true);
  };

  const downloadTemplate = () => {
    window.open(`${API}/users/template/excel`, '_blank');
  };

  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {filteredUsers.map(user => (
        <Card key={user.id} className="p-5 border hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-gray-50">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                  {user.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">{user.name}</h4>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-2 mb-3">
            {user.gk_code && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">GK Code:</span>
                <span className="font-semibold text-gray-900">{user.gk_code}</span>
              </div>
            )}
            {user.module && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Module:</span>
                <span className="font-semibold text-gray-900 capitalize">{user.module}</span>
              </div>
            )}
            {user.center_id && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Center:</span>
                <span className="font-semibold text-gray-900 truncate ml-2">{centers.find(c => c.id === user.center_id)?.name || 'N/A'}</span>
              </div>
            )}
            {isValidDate(user.date_of_birth) && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Age:</span>
                <span className="font-semibold text-gray-900">
                  {Math.floor((new Date() - new Date(user.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))} years
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
              {user.role}
            </span>
            {user.is_center_head && (
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                Center Head
              </span>
            )}
            {user.status && (
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                user.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                user.status === 'on_break' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {user.status === 'on_break' ? 'On Break' : user.status === 'non_active' ? 'Non Active' : 'Active'}
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => handleViewInfo(user)}
              variant="outline"
              size="sm"
              className="flex-1"
            >
              <Eye className="w-4 h-4 mr-1" />
              View
            </Button>
            <Button
              onClick={() => handleEdit(user)}
              variant="outline"
              size="sm"
              className="flex-1"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
            <Button
              onClick={() => handleDelete(user.id)}
              variant="outline"
              size="sm"
              className="text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
      <table className="w-full">
        <thead className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
          <tr>
            <th className="px-6 py-3 text-left text-sm font-semibold">Name</th>
            <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
            {activeRole === 'student' && (
              <>
                <th className="px-6 py-3 text-left text-sm font-semibold">GK Code</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Module</th>
              </>
            )}
            <th className="px-6 py-3 text-left text-sm font-semibold">Center</th>
            <th className="px-6 py-3 text-left text-sm font-semibold">Status</th>
            <th className="px-6 py-3 text-center text-sm font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {filteredUsers.map((user, idx) => (
            <tr key={user.id} className={`hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{user.name}</span>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
              {activeRole === 'student' && (
                <>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">{user.gk_code || 'N/A'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 capitalize">{user.module || 'N/A'}</td>
                </>
              )}
              <td className="px-6 py-4 text-sm text-gray-600">
                {centers.find(c => c.id === user.center_id)?.name || 'N/A'}
              </td>
              <td className="px-6 py-4">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  user.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                  user.status === 'on_break' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {user.status === 'on_break' ? 'On Break' : user.status === 'non_active' ? 'Non Active' : user.status || 'Active'}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleViewInfo(user)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="View Info"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(user)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderBlockView = () => (
    <div className="grid grid-cols-1 gap-3">
      {filteredUsers.map(user => (
        <Card key={user.id} className="p-4 border hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl">
                {user.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-gray-900 text-lg">{user.name}</h4>
                <p className="text-sm text-gray-500">{user.email}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {user.gk_code && <span className="text-xs bg-gray-100 px-2 py-1 rounded">GK: {user.gk_code}</span>}
                  {user.module && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded capitalize">{user.module}</span>}
                  {user.is_center_head && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Center Head</span>}
                  <span className={`text-xs px-2 py-1 rounded ${
                    user.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                    user.status === 'on_break' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {user.status === 'on_break' ? 'On Break' : user.status === 'non_active' ? 'Non Active' : user.status || 'Active'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handleViewInfo(user)} variant="outline" size="sm">
                <Eye className="w-4 h-4" />
              </Button>
              <Button onClick={() => handleEdit(user)} variant="outline" size="sm">
                <Edit className="w-4 h-4" />
              </Button>
              <Button onClick={() => handleDelete(user.id)} variant="outline" size="sm" className="text-red-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  // Profile validation removed from admin panel

  return (
    <div className="space-y-6">
      <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              User Management
            </h2>
            <p className="text-sm text-gray-600 mt-1">Manage teachers and students across all centers</p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm border">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'list' 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Grid View"
              >
                <Grid3x3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('block')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'block' 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Block View"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>

            {/* Import/Export Buttons */}
            <Button
              onClick={downloadTemplate}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Template
            </Button>
            <Button
              onClick={() => setImportDialog(true)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Import
            </Button>
            
            {/* Add User Button */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                  onClick={() => {
                    resetForm();
                    setEditingUser(null);
                  }}
                >
                  <UserPlus className="w-4 h-4" />
                  Add {activeRole.charAt(0).toUpperCase() + activeRole.slice(1)}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="password">Password {editingUser && '(leave blank to keep current)'}</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUser}
                    />
                  </div>
                  {formData.role === 'teacher' && (
                    <>
                      <div>
                        <Label htmlFor="center">Center</Label>
                        <Select value={formData.center_id} onValueChange={(value) => setFormData({ ...formData, center_id: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select center" />
                          </SelectTrigger>
                          <SelectContent>
                            {centers.map(center => (
                              <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="is_center_head"
                          checked={formData.is_center_head}
                          onChange={(e) => setFormData({ ...formData, is_center_head: e.target.checked })}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <Label htmlFor="is_center_head" className="cursor-pointer">Center Head</Label>
                      </div>
                      
                      {/* Additional Teacher Fields */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="contact_number">Contact Number</Label>
                          <Input
                            id="contact_number"
                            value={formData.contact_number}
                            onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                            placeholder="Phone number"
                          />
                        </div>
                        <div>
                          <Label htmlFor="alternate_contact_number">Alternate Contact</Label>
                          <Input
                            id="alternate_contact_number"
                            value={formData.alternate_contact_number}
                            onChange={(e) => setFormData({ ...formData, alternate_contact_number: e.target.value })}
                            placeholder="Alternate number"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="full_address">Full Address</Label>
                          <Input
                            id="full_address"
                            value={formData.full_address}
                            onChange={(e) => setFormData({ ...formData, full_address: e.target.value })}
                            placeholder="Complete address"
                          />
                        </div>
                        <div>
                          <Label htmlFor="postal_code">Pincode</Label>
                          <Input
                            id="postal_code"
                            value={formData.postal_code}
                            onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                            placeholder="Postal code"
                            maxLength={6}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="date_of_birth">Date of Birth</Label>
                          <Input
                            id="date_of_birth"
                            type="date"
                            value={isValidDate(formData.date_of_birth) ? new Date(formData.date_of_birth).toISOString().split('T')[0] : ''}
                            onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value ? new Date(e.target.value).toISOString() : null })}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <Label htmlFor="date_of_joining">Date of Joining</Label>
                          <Input
                            id="date_of_joining"
                            type="date"
                            value={isValidDate(formData.date_of_joining) ? new Date(formData.date_of_joining).toISOString().split('T')[0] : ''}
                            onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value ? new Date(e.target.value).toISOString() : null })}
                            className="w-full"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="gender">Gender</Label>
                        <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="status">Status</Label>
                        <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="on_break">On Break</SelectItem>
                            <SelectItem value="non_active">Non Active</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                  {formData.role === 'student' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="gk_code">GK Code</Label>
                          <Input
                            id="gk_code"
                            value={formData.gk_code}
                            onChange={(e) => setFormData({ ...formData, gk_code: e.target.value })}
                            placeholder="e.g., GK001"
                          />
                        </div>
                        <div>
                          <Label htmlFor="module">Module</Label>
                          <Select value={formData.module} onValueChange={(value) => setFormData({ ...formData, module: value })}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select module" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="toddler">Toddler</SelectItem>
                              <SelectItem value="junior">Junior</SelectItem>
                              <SelectItem value="senior">Senior</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {!editingUser && (
                        <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="request_kit"
                              checked={formData.request_kit}
                              onChange={(e) => setFormData({ ...formData, request_kit: e.target.checked })}
                              className="w-4 h-4 rounded border-gray-300"
                            />
                            <Label htmlFor="request_kit" className="cursor-pointer font-medium text-blue-900">
                              Request Kit for this Student
                            </Label>
                          </div>
                          {formData.request_kit && (
                            <div>
                              <Label htmlFor="kit_notes" className="text-sm text-blue-800">Kit Request Notes (Optional)</Label>
                              <input
                                type="text"
                                id="kit_notes"
                                value={formData.kit_notes}
                                onChange={(e) => setFormData({ ...formData, kit_notes: e.target.value })}
                                placeholder="Add any special notes for the kit request..."
                                className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm mt-1"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-purple-600">
                    {editingUser ? 'Update User' : 'Create User'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs value={activeRole} onValueChange={setActiveRole} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-white">
            <TabsTrigger value="teacher" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">
              Teachers ({users.filter(u => u.role === 'teacher').length})
            </TabsTrigger>
            <TabsTrigger value="student" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">
              Students ({users.filter(u => u.role === 'student').length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeRole} className="mt-6">
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder={`Search ${activeRole}s by name, email, ${activeRole === 'student' ? 'GK code, module' : 'center'}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 py-3 w-full border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
              </div>
              {searchTerm && (
                <p className="text-sm text-gray-600 mt-2 font-medium">
                  Found {filteredUsers.length} result(s)
                </p>
              )}
            </div>

            {filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No {activeRole}s found</p>
              </div>
            ) : (
              <>
                {viewMode === 'grid' && renderGridView()}
                {viewMode === 'list' && renderListView()}
                {viewMode === 'block' && renderBlockView()}
              </>
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Import Dialog */}
      <Dialog open={importDialog} onOpenChange={setImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Users from Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <span className="text-blue-600 font-semibold hover:text-blue-700">
                  Click to upload
                </span>
                <span className="text-gray-600"> or drag and drop</span>
              </label>
              {importFile && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {importFile.name}
                </p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="update_existing"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <Label htmlFor="update_existing" className="cursor-pointer">
                Update existing users (match by email)
              </Label>
            </div>

            <div className="flex gap-3">
              <Button onClick={handleFileUpload} className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600">
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
              <Button onClick={() => setImportDialog(false)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Info Dialog */}
      <Dialog open={viewInfoDialog} onOpenChange={setViewInfoDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Information</DialogTitle>
          </DialogHeader>
          {selectedUserInfo && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl">
                  {selectedUserInfo.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{selectedUserInfo.name}</h3>
                  <p className="text-gray-600">{selectedUserInfo.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Role</p>
                  <p className="font-semibold capitalize">{selectedUserInfo.role}</p>
                </div>
                {selectedUserInfo.gk_code && (
                  <div>
                    <p className="text-sm text-gray-600">GK Code</p>
                    <p className="font-semibold">{selectedUserInfo.gk_code}</p>
                  </div>
                )}
                {selectedUserInfo.module && (
                  <div>
                    <p className="text-sm text-gray-600">Module</p>
                    <p className="font-semibold capitalize">{selectedUserInfo.module}</p>
                  </div>
                )}
                {selectedUserInfo.center_id && (
                  <div>
                    <p className="text-sm text-gray-600">Center</p>
                    <p className="font-semibold">{centers.find(c => c.id === selectedUserInfo.center_id)?.name || 'N/A'}</p>
                  </div>
                )}
                {selectedUserInfo.contact_number && (
                  <div>
                    <p className="text-sm text-gray-600">Contact</p>
                    <p className="font-semibold">{selectedUserInfo.contact_number}</p>
                  </div>
                )}
                {isValidDate(selectedUserInfo.date_of_birth) && (
                  <div>
                    <p className="text-sm text-gray-600">Date of Birth</p>
                    <p className="font-semibold">{new Date(selectedUserInfo.date_of_birth).toLocaleDateString()}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full inline-block ${
                    selectedUserInfo.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                    selectedUserInfo.status === 'on_break' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {selectedUserInfo.status === 'on_break' ? 'On Break' : selectedUserInfo.status === 'non_active' ? 'Non Active' : selectedUserInfo.status || 'Active'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Errors Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Errors ({importErrors.length})</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-gray-600 mb-4">
              The following rows had errors and were not imported. Please fix these issues in your Excel file and try again.
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {importErrors.map((error, index) => (
                <div key={index} className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                  {error}
                </div>
              ))}
            </div>
            <Button onClick={() => setShowErrorDialog(false)} className="w-full mt-4">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;